import { ThemeProvider } from 'theme-ui';
import { ThemeProvider as GrowthThemeProvider } from '@picsart/growth-rc/theming';
import { Global } from '@emotion/core';
import { FC, useCallback, useEffect, useRef } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { hot } from 'react-hot-loader';
import { store } from './store';
import { theme } from './theme';
import { globalStyles } from './GlobalStyle';
import { ReactINTLProvider } from '@picsart/rc/services/localization';

import Loading, { LoadingMode } from '@picsart/rc/old/Loading';
import { RegistrationModal } from '@picsart/growth-rc/Registration';

import { ModalBasic } from 'shared/Modal';
import { setUser } from 'modules/user';
import { User } from 'types/User';
import { MainContainer } from './components/MainContainer';
import { isSettingsFetched } from 'modules/settings';
import { getAvailableTransitions } from 'modules/transitions';
import { Notifier, DirectionEnum } from '@picsart/rc/old/Notifier';
import { NotifierMessageProvider } from './NotifierMessageProvider';
import DragAndDropUploader from '@picsart/rc/old/DragAndDropUploader';
import { ACCEPTED_AUDIO, ACCEPTED_CONTENT } from 'constants/filetypes';
import { noop } from 'utils/noop';
import { uploadFiles } from 'modules/scenes';
import { addAlert } from 'modules/alert';
import { uploadFileMessages } from 'constants/errorMessages';
import { cancelUploadUserSong, uploadUserTrack } from 'modules/userSongs';
import analytics from 'utils/analytics';
import createUseStyles from '@picsart/rc/create';
import { deepMergeStyles } from '@picsart/rc/helpers';
import resets from '@picsart/rc/resets';
import { withFontFamily, GilroyFonts } from '@picsart/rc/fonts';
import colors from '@picsart/rc/colors';
import aimStyles from '@picsart/rc/aim';
import { PlayerContextProvider } from 'utils/PlayerContext';
import { fetchUserData } from 'utils/apiWrapper';
import { useAsync, useMount } from 'react-use';
import { isNil } from 'ramda';
import { getUrlParametr, hasUrlParametr } from 'utils/utils';
import ErrorLogger from './components/ErrorLogger';
import { UserModel } from '@picsart/rc/types/models/user';
import { changeThemeColors } from './colors';
import { useAuthContext } from '@picsart/growth-rc/AuthContext';

const useStyles = createUseStyles(
  {
    ...deepMergeStyles(resets, colors, aimStyles, withFontFamily(GilroyFonts))
  },
  {
    name: 'app'
  }
);

changeThemeColors();

const ErrorBoundary = ErrorLogger.getErrorBoundary();
const StateDecorator: <T = any>(c: FC<T>) => FC<T> = Comp => props => (
  <ReactINTLProvider projectName="video_editor">
    <Provider store={store}>
      <Comp {...props} />
    </Provider>
  </ReactINTLProvider>
);

const verticalOffset = {
  xl: 10,
  lg: 20,
  md: 30,
  sm: 40
};
const horizontalOffset = {
  xl: 11,
  lg: 22,
  md: 33,
  sm: 44
};
const useAuthModal = (): {
  isOpen: boolean;
  isFetching: boolean;
  user?: User;
  setUserHandler: (user: User) => void;
} => {
  const dispatch = useDispatch();
  const { tokenDataRef, user } = useAuthContext();
  const setUserHandler = useCallback((data: UserModel) => {
    dispatch(setUser({ ...data, key: tokenDataRef?.current?.access_token }));
  }, []);

  const { loading: isFetching } = useAsync(async () => {
    const data = await fetchUserData();

    setUserHandler(user);

    if (data) return;

    if (!window.analytics) {
      window.analytics = {};
    }

    window.analytics.isVideoEditorLoadDispatched = true;

    analytics.videoEditorLoad({
      loading_time: Math.round(performance.now()),
      is_logged_in: false
    });
  }, []);
  // @ts-ignore
  return { isOpen: !isFetching && isNil(user), isFetching, setUserHandler, user };
};

const Root = () => {
  const dispatch = useDispatch();
  const styleRef = useRef<HTMLStyleElement | null>(null);

  useEffect(() => {
    styleRef.current?.remove();
    styleRef.current = changeThemeColors();
    document.head.appendChild(styleRef.current);
  }, []);

  useMount(() => () => styleRef.current?.remove());
  useStyles();

  const isSettingsLoaded = useSelector(isSettingsFetched);
  const isTransitionsLoaded = useSelector(getAvailableTransitions)?.length > 0;
  const {
    isOpen: isAuthModalOpened,
    isFetching: isFetchingUser,
    setUserHandler,
    user
  } = useAuthModal();

  const isLoaderVisible = isFetchingUser || !isTransitionsLoaded || !isSettingsLoaded;

  const onDropRejected = useCallback((files: { file: File; errors: any[] }[]) => {
    files.forEach(file => {
      if (file.file.type.includes('gif')) {
        analytics.uploadDone({ item: 'image', result: 'fail' });
      }
    });
    dispatch(addAlert(uploadFileMessages.title, uploadFileMessages.message));
  }, []);

  const handleUpload = useCallback((files: File[]) => {
    const audio = files.find(item => item.type === ACCEPTED_AUDIO);
    const content = files.filter(item => item.type !== ACCEPTED_AUDIO);
    audio && dispatch(uploadUserTrack(audio));
    content.length > 0 && dispatch(uploadFiles(content));
  }, []);

  useEffect(() => {
    if (!window?.analytics?.isVideoEditorLoadDispatched) {
      const source = hasUrlParametr('create') ? 'create_flow' : getUrlParametr('referrer');
      analytics.open(user?.subscribed ?? false, source || 'video_editor');

      if (!window.analytics) {
        window.analytics = {};
      }

      window.analytics.isVideoEditorLoadDispatched = true;
    }
  }, [user]);

  useEffect(() => {
    window.addEventListener('beforeunload', () => dispatch(cancelUploadUserSong()));
  }, []);

  useEffect(() => {
    window.zE && window.zE('webWidget', 'hide');
  }, [window.zE]);

  return (
    <GrowthThemeProvider>
      <ThemeProvider theme={theme}>
        <Global styles={globalStyles} />
        <ErrorBoundary>
          <Notifier
            direction={DirectionEnum.bottomLeft}
            verticalOffset={verticalOffset}
            horizontalOffset={horizontalOffset}
            sx={{ zIndex: 'notifier' }}
          >
            <NotifierMessageProvider>
              <ModalBasic opened={isAuthModalOpened}>
                <div data-test="modal root">
                  <RegistrationModal
                    isOpen={isAuthModalOpened}
                    triggeredFrom="cta"
                    authSuccessHandler={setUserHandler}
                  />
                </div>
              </ModalBasic>
              <PlayerContextProvider>
                <DragAndDropUploader
                  isDragDisabled={false}
                  onDrop={noop}
                  onDropAccepted={handleUpload}
                  onDropRejected={onDropRejected as any} // bug in typings for the prop
                  allowedTypes={[ACCEPTED_AUDIO, ...ACCEPTED_CONTENT.split(',')]}
                >
                  {isLoaderVisible && <Loading mode={LoadingMode.Cover} />}
                  {user && isSettingsLoaded && isTransitionsLoaded && <MainContainer />}
                </DragAndDropUploader>
              </PlayerContextProvider>
            </NotifierMessageProvider>
          </Notifier>
        </ErrorBoundary>
      </ThemeProvider>
    </GrowthThemeProvider>
  );
};

export default StateDecorator(hot(module)(Root));
